import React, { useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DOMPurify from "dompurify"; // Ochrana proti XSS
import TrainingSummaryPDF from "./TrainingSummaryPDF";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { ExerciseContext } from "../context/ExerciseContext";

const MAX_COMMENT_LENGTH = 500;

const SummaryScreen = () => {
    const navigate = useNavigate();
    const { selectedExercises } = useContext(ExerciseContext);
    const [rating, setRating] = useState(null);
    const [comment, setComment] = useState("");
    const location = useLocation();
    const { numSets } = location.state || { numSets: 1 };

    const validExercises = selectedExercises.filter(Boolean);
    const avgCardio =
        validExercises.length > 0
            ? validExercises.reduce((acc, ex) => acc + (ex.cardio || 0), 0) / validExercises.length
            : 0;
    const avgStrength =
        validExercises.length > 0
            ? validExercises.reduce((acc, ex) => acc + (ex.strength || 0), 0) / validExercises.length
            : 0;

    const getMotivationalMessage = () => {
        if (rating === "amazing") return "Skvělá práce! Překonal jsi sám sebe!";
        if (rating === "good") return "Super výkon! Každý trénink tě posouvá dál!";
        if (rating === "tough") return "To bylo náročné, ale zvládl jsi to! Respekt!";
        return "Great job! Respect!";
    };

    const sanitizedComment = DOMPurify.sanitize(comment);

    // Funkce pro sdílení
    const shareOnX = () => {
        const text = `I just finished my Tabata Workout! 💪🔥\n\nTry it too at https://tabataworkout.online`;
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, "_blank");
    };

    const shareOnFacebook = () => {
        const url = encodeURIComponent("https://tabataworkout.online");
        const quote = encodeURIComponent("I just finished my Tabata session! 💪🔥 Can you beat it?");
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`, "_blank");
    };

    const shareOnInstagram = () => {
        alert("Instagram doesn’t support sharing via URL. Save the image or PDF to share it manually.");
    };

    return (
        <div className="exercise-selector-container">
            {/* Pozadí */}
            <img src="/images/background.jpg" alt="background" className="bg-video" />
            <div className="overlay"></div>

            {/* Logo nahoře */}
            <div className="logo-container">
                <a href="https://www.tabataworkout.online">
                    <img src="/images/logo.png" alt="Logo" className="logo" />
                </a>
            </div>

            {/* Shrnutí */}
            <div className="summary-container" style={{ textAlign: "center", padding: "20px" }}>
                <h2>{getMotivationalMessage()}</h2>

                <div style={{ marginTop: "20px" }}>
                    <h4>Exercises Completed</h4>
                    {selectedExercises.length > 0 ? (
                        <div>
                            {selectedExercises.map((exercise, index) => (
                                <p key={index}>{exercise?.name || "Unknown exercise"}</p>
                            ))}
                        </div>
                    ) : (
                        <p>No exercises were completed.</p>
                    )}
                </div>

                <div style={{ marginTop: "20px" }}>
                    <h4>Want to jot something down?</h4>
                    <textarea
                        value={comment}
                        onChange={(e) => {
                            if (e.target.value.length <= MAX_COMMENT_LENGTH) {
                                setComment(e.target.value);
                            }
                        }}
                        placeholder="For example: I felt great, but the last circuit was the toughest!"
                        style={{ width: "50%", height: "60px", padding: "10px" }}
                    />
                    <p>{comment.length}/{MAX_COMMENT_LENGTH} characters</p>
                </div>

                <div style={{ marginTop: "30px" }}>
                    <h3>🔥 Ready for the next challenge? 🔥</h3>
                </div>

                {/* Tlačítka a ikony */}
                <div style={{ marginTop: "20px", display: "flex", justifyContent: "center", gap: "15px", flexWrap: "wrap" }}>
                    <PDFDownloadLink
                        document={
                            <TrainingSummaryPDF
                                exercises={selectedExercises}
                                feeling={rating || "Not entered"}
                                comment={sanitizedComment || "No comment"}
                                avgCardio={avgCardio}
                                avgStrength={avgStrength}
                                numSets={numSets}
                            />
                        }
                        fileName={`TabataWorkout-Summary-${new Date().toISOString().slice(0,10)}.pdf`}
                        style={{
                            fontSize: "16px",
                            textDecoration: "none",
                            padding: "10px 20px",
                            backgroundColor: "#1c192c",
                            color: "white",
                            border: "none",
                            borderRadius: "5px",
                            cursor: "pointer"
                        }}
                    >
                        {({ loading }) => (loading ? "Generating PDF…" : "📄 Download workout summary as PDF")}
                    </PDFDownloadLink>

                    <button
                        onClick={() => navigate("/")}
                        style={{
                            fontSize: "16px",
                            padding: "10px 20px",
                            backgroundColor: "#E51937",
                            color: "white",
                            border: "none",
                            borderRadius: "5px",
                            cursor: "pointer"
                        }}
                    >
                        Start next Tabata!
                    </button>

                    {/* Text před ikonou návratu */}
                    <span style={{ alignSelf: "center", fontSize: "16px" }}>Go to:</span>

                    {/* Ikona návratu na úvodní stránku */}
                    <a href="https://www.tabataworkout.online" style={{ display: "inline-block", cursor: "pointer" }}>
                        <img src="/images/default.gif" alt="Home" style={{ width: "50px", height: "50px" }} />
                    </a>

                    {/* Text před sociálními ikonami */}
                    <span style={{ alignSelf: "center", fontSize: "16px" }}>Share on:</span>

                    {/* Ikony sociálních sítí */}
                    <button onClick={shareOnFacebook} style={{ background: "none", border: "none", cursor: "pointer" }}>
                        <img src="/images/icon-facebook.png" alt="Facebook" style={{ width: "50px", height: "50px" }} />
                    </button>

                    <button onClick={shareOnInstagram} style={{ background: "none", border: "none", cursor: "pointer" }}>
                        <img src="/images/icon-instagram.png" alt="Instagram" style={{ width: "50px", height: "50px" }} />
                    </button>

                    <button onClick={shareOnX} style={{ background: "none", border: "none", cursor: "pointer" }}>
                        <img src="/images/icon-x.png" alt="X" style={{ width: "50px", height: "50px" }} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SummaryScreen;
